<template>
  <span class="kpi-animated-number">{{ display }}</span>
</template>

<script>
/**
 * Um número que caminha até o valor novo em vez de saltar.
 *
 * ONDE USAR: só na tira de totais. São seis números, e é ali que o movimento
 * explica alguma coisa — ao trocar de competência ou de filtro, ver o total
 * subir mostra que a tela recalculou, e para onde. Aplicar isto às células da
 * tabela seria o oposto: 2.687 linhas × 13 colunas animando ao mesmo tempo é
 * exatamente o tipo de "polimento" que faz a tela travar.
 *
 * O tween é em requestAnimationFrame e termina cravando o valor de destino, sem
 * depender de quantos quadros couberam: número financeiro não pode parar em
 * 47.999,97 porque a aba perdeu o foco no meio.
 *
 * Com prefers-reduced-motion o valor troca direto. A duração e a curva batem
 * com o resto da tela (220ms, ease-out).
 */
const DURACAO = 420

const easeOut = t => 1 - (1 - t) ** 3

export default {
  name: 'KpiAnimatedNumber',
  props: {
    value: {
      type: Number,
      default: 0,
    },
    /** Recebe o número e devolve o texto — moeda, percentual, inteiro. */
    formatter: {
      type: Function,
      default: value => value,
    },
  },
  data() {
    return {
      current: this.value,
    }
  },
  computed: {
    display() {
      return this.formatter(this.current)
    },
  },
  watch: {
    value(destino, origem) {
      this.tween(origem, destino)
    },
  },
  beforeDestroy() {
    this.cancel()
  },
  methods: {
    prefersReducedMotion() {
      return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    cancel() {
      if (this.frame) {
        cancelAnimationFrame(this.frame)
        this.frame = null
      }
    },
    tween(origem, destino) {
      this.cancel()

      if (!Number.isFinite(destino)) {
        this.current = destino
        return
      }

      if (this.prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
        this.current = destino
        return
      }

      const partida = Number.isFinite(origem) ? origem : 0
      const inicio = performance.now()

      const passo = agora => {
        const decorrido = Math.min(1, (agora - inicio) / DURACAO)
        this.current = partida + (destino - partida) * easeOut(decorrido)

        if (decorrido < 1) {
          this.frame = requestAnimationFrame(passo)
          return
        }

        // Crava o destino: o valor exibido nunca pode ser o do penúltimo quadro.
        this.current = destino
        this.frame = null
      }

      this.frame = requestAnimationFrame(passo)
    },
  },
}
</script>

<style lang="scss" scoped>
.kpi-animated-number {
  font-variant-numeric: tabular-nums;
}
</style>
